import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  Utensils,
  Accessibility,
  Calendar,
  MapPin,
  Users,
  AlertCircle,
  ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventSummaryPage = () => {
  const { shareToken } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSummary();
  }, [shareToken]);

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API}/event-summary/${shareToken}`);
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      } else {
        const err = await res.json();
        setError(err.detail || "Summary not found");
      }
    } catch (err) {
      setError("Failed to load summary");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gray-800 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Summary Not Available</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalDietary = Object.values(summary.dietary_summary || {}).reduce((a, b) => a + b, 0);
  const totalAccessibility = Object.values(summary.accessibility_summary || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-semibold text-lg">Event Summary</span>
          </div>
          <Badge variant="secondary" className="text-xs">
            View Only
          </Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Event Info */}
        <Card className="mb-6">
          <CardContent className="py-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{summary.event_name}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-gray-700">
              {summary.event_date && (
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {new Date(summary.event_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </div>
              )}
              {summary.event_location && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {summary.event_location}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                {summary.total_guests} guests
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="py-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-orange-100 flex items-center justify-center">
                <Utensils className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{totalDietary}</p>
              <p className="text-sm text-gray-600">Dietary Needs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-blue-100 flex items-center justify-center">
                <Accessibility className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{totalAccessibility}</p>
              <p className="text-sm text-gray-600">Accessibility Needs</p>
            </CardContent>
          </Card>
        </div>

        {/* Dietary Breakdown */}
        {Object.keys(summary.dietary_summary || {}).length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Utensils className="w-4 h-4 text-orange-600" />
                Dietary Requirements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(summary.dietary_summary)
                  .sort((a, b) => b[1] - a[1])
                  .map(([need, count]) => {
                    const percentage = Math.round((count / summary.total_guests) * 100);
                    const isAllergy = need.toLowerCase().includes('allergy') || need.toLowerCase().includes('free');
                    
                    return (
                      <div key={need}>
                        <div className="flex justify-between text-sm mb-1.5">
                          <span className={`capitalize flex items-center gap-1.5 ${isAllergy ? "font-medium text-red-700" : ""}`}>
                            {isAllergy && <AlertCircle className="w-3 h-3 text-red-500" />}
                            {need.replace(/_/g, " ")}
                          </span>
                          <span className="text-gray-700">{count} guests ({percentage}%)</span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${isAllergy ? "bg-red-500" : "bg-orange-500"}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Accessibility Breakdown */}
        {Object.keys(summary.accessibility_summary || {}).length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Accessibility className="w-4 h-4 text-blue-600" />
                Accessibility Accommodations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(summary.accessibility_summary)
                  .sort((a, b) => b[1] - a[1])
                  .map(([need, count]) => {
                    const percentage = Math.round((count / summary.total_guests) * 100);
                    
                    return (
                      <div key={need}>
                        <div className="flex justify-between text-sm mb-1.5">
                          <span className="capitalize font-medium">{need.replace(/_/g, " ")}</span>
                          <span className="text-gray-700">{count} guests ({percentage}%)</span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Data State */}
        {Object.keys(summary.dietary_summary || {}).length === 0 && 
         Object.keys(summary.accessibility_summary || {}).length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-gray-600">
              <p>No special accommodations reported yet.</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center border-t bg-white">
        <a 
          href="https://phygital.systems" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-700 transition-colors"
        >
          <PhygitalIcon className="w-5 h-5" />
          <span>Powered by Phygital Event OS</span>
          <ExternalLink className="w-3 h-3" />
        </a>
        <p className="text-xs text-gray-500 mt-2">
          Generated {new Date(summary.generated_at).toLocaleString()}
        </p>
      </footer>
    </div>
  );
};

export default EventSummaryPage;
